import React from 'react';
import { Box, Typography, Grid, Link, Divider, Card, CardContent } from '@mui/material';
import {
  Smartphone as AndroidIcon,
  Apple as IosIcon,
  Code as CodeIcon,
  Palette as PluginIcon,
  Shop as ShopifyIcon,
  ShoppingCart as WoocommerceIcon,
  LocalShipping as MagentoIcon,
  Language as WixIcon,
  CheckCircleOutline as CheckmarkIcon,
  FlashOn as ExpressIcon,
  Http as HostedIcon,
  Business as MerchantIcon,
  Description as InvoiceIcon,
  Link as LinkIcon,
  Send as EmbedIcon,
} from '@mui/icons-material';

// Custom SDK Icons - Using general icons as substitutes
const ReactIcon = (props) => <CodeIcon style={{ color: '#61DAFB' }} {...props} />;
const FlutterIcon = (props) => <CodeIcon style={{ color: '#02569B' }} {...props} />;
const CordovaIcon = (props) => <CodeIcon style={{ color: '#E3E8FF' }} {...props} />;

// Custom Plugin Icons - Using general icons as substitutes
const ZutopayIcon = (props) => <CheckmarkIcon style={{ color: '#38A169' }} {...props} />; // Placeholder
const ServerIcon = (props) => <CodeIcon style={{ color: '#5A67D8' }} {...props} />; // Placeholder


const PaymentIntegrationTab = () => {
  const integrationItems = [
    { icon: <AndroidIcon color="success" />, text: 'Android SDK' },
    { icon: <IosIcon />, text: 'iOS SDK' },
    { icon: <ReactIcon />, text: 'React Native SDK' },
    { icon: <FlutterIcon />, text: 'Flutter SDK' },
    { icon: <CordovaIcon />, text: 'Cordova SDK' },
  ];

  const codeSolutionItems = [
    { icon: <LinkIcon color="primary" />, text: 'Create Payment Link' },
    { icon: <EmbedIcon color="primary" />, text: 'Embed a Payment Buttons' },
    { icon: <InvoiceIcon color="primary" />, text: 'Invoice your customers' },
  ];

  const pluginItems = [
    { icon: <ShopifyIcon style={{ color: '#96b63d' }} />, text: 'Shopify' },
    { icon: <WoocommerceIcon style={{ color: '#9658A4' }} />, text: 'WooCommerce' },
    { icon: <MagentoIcon style={{ color: '#EB6E2F' }} />, text: 'Magento' },
    { icon: <WixIcon style={{ color: '#272F32' }} />, text: 'Wix' },
  ];

  const checkoutBoxes = [
    { icon: <ZutopayIcon />, title: 'Zutopay Hosted Checkout', link: '#' },
    { icon: <CheckmarkIcon style={{ color: '#38A169' }} />, title: 'Checkout Plus', link: '#' },
    { icon: <ServerIcon />, title: 'Server Side Packages', link: '#' },
    { icon: <MerchantIcon color="success" />, title: 'Merchant Hosted Checkout', link: '#' },
    { icon: <ExpressIcon style={{ color: '#F6E05E' }} />, title: 'Checkout Express', link: '#' },
  ];

  const CheckoutBox = ({ icon, title, link }) => (
    <Grid item size={{ xs: 12, sm: 6, md: 4 }} sx={{ pr: { xs: 0, sm: 2 }, pb: { xs: 2, sm: 0 } }}>
      <Box
        component={Link}
        href={link}
        target="_blank"
        rel="noopener"
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          border: '1px solid #e0e0e0',
          borderRadius: 1,
          textDecoration: 'none',
          color: 'text.primary',
          '&:hover': {
            borderColor: 'primary.main',
            boxShadow: 1,
          },
          transition: 'border-color 0.2s, box-shadow 0.2s',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {React.cloneElement(icon, { sx: { mr: 1, fontSize: 24 } })}
          <Typography variant="body1" fontWeight="medium">{title}</Typography>
        </Box>
        <Box
          component="span"
          sx={{
            ml: 1,
            color: 'primary.main',
            fontSize: 14,
            '&::after': { content: '"\\2197"' } // Unicode for the external link arrow
          }}
        />
      </Box>
    </Grid>
  );


  const IntegrationItem = ({ icon, text }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
      {React.cloneElement(icon, { sx: { mr: 1.5, fontSize: 20 } })}
      <Typography variant="body2">{text}</Typography>
    </Box>
  );

  const SolutionLink = ({ icon, text }) => (
    <Link
      href="#"
      target="_blank"
      rel="noopener"
      underline="none"
      sx={{
        display: 'flex',
        alignItems: 'center',
        mb: 1.5,
        color: 'text.primary',
        '&:hover': { color: 'primary.main' }
      }}
    >
      {React.cloneElement(icon, { sx: { mr: 1.5, fontSize: 16 } })}
      <Typography variant="body2">{text}</Typography>
      <Box
        component="span"
        sx={{
          ml: 0.5,
          color: 'primary.main',
          fontSize: 10,
          '&::after': { content: '"\\2197"' } // Unicode for the external link arrow
        }}
      />
    </Link>
  );


  return (
    <Box sx={{ maxWidth: 1200, margin: '0 auto', px: { xs: 2, md: 4 }, py: 4 }}>
      {/* Main Title Section - Moved outside the card */}
      <Box sx={{ mb: 4, textAlign: 'left' }}>
        <Typography variant="h4" component="h1"  sx={{ mb: 0.5, lineHeight: 1.2 , fontWeight:500 }}>
          Simplify Payment Processing
        </Typography>
        <Typography variant="h4" component="h2"  color="text.secondary" sx={{ lineHeight: 1.2 , fontWeight:500}}>
          for Your Business
        </Typography>
      </Box>

      <Card variant="outlined" sx={{ borderRadius: 2, bgcolor: '#f8f8f8' }}>
        <CardContent>
          {/* --- Top Section: Integrations, Code, Plugins --- */}
          <Grid container spacing={2} sx={{ mb: 4 }}>
            {/* Integration On Your App */}
            <Grid item size={{ xs: 12, md: 4 }}>
              <Typography variant="subtitle1" fontWeight="bold" color="success.main" sx={{ mb: 2 }}>
                Integrate On Your App
              </Typography>
              {integrationItems.map((item, index) => (
                <IntegrationItem key={index} icon={item.icon} text={item.text} />
              ))}
            </Grid>

            {/* Vertical Divider 1 */}
            <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />
            <Box sx={{ display: { xs: 'block', md: 'none' }, width: '100%' }}>
              <Divider sx={{ my: 2 }} />
            </Box>

            {/* Code Solution */}
            <Grid item size={{ xs: 12, md: 3.5 }}>
              <Typography variant="subtitle1" fontWeight="bold" color="success.main" sx={{ mb: 2 }}>
                Code Solution
              </Typography>
              {codeSolutionItems.map((item, index) => (
                <SolutionLink key={index} icon={item.icon} text={item.text} />
              ))}
            </Grid>

            {/* Vertical Divider 2 */}
            <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />
            <Box sx={{ display: { xs: 'block', md: 'none' }, width: '100%' }}>
              <Divider sx={{ my: 2 }} />
            </Box>

            {/* Plugins */}
            <Grid item size={{ xs: 12, md: 3.5 }}>
              <Typography variant="subtitle1" fontWeight="bold" color="success.main" sx={{ mb: 2 }}>
                Plugins
              </Typography>
              {pluginItems.map((item, index) => (
                <IntegrationItem key={index} icon={item.icon} text={item.text} />
              ))}
            </Grid>
          </Grid>

          {/* Horizontal Divider */}
          <Divider sx={{ my: 3 }} />

          {/* --- Bottom Section: Build a Check On Web --- */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" fontWeight="bold" color="success.main" sx={{ mb: 2 }}>
              Build a Check On Web
            </Typography>

            <Grid container spacing={2}>
              {checkoutBoxes.map((box, index) => (
                <CheckoutBox
                  key={index}
                  icon={box.icon}
                  title={box.title}
                  link={box.link}
                />
              ))}
            </Grid>
          </Box>

        </CardContent>
      </Card>
    </Box>
  );
};

export default PaymentIntegrationTab;